import React, { useRef, useState, useEffect } from "react";
import BannerSectionStyle3 from "../Section/BannerSection/BannerSectionStyle3";
import { pageTitle } from "../../helpers/PageTitle";
import "./ResearchandDevelopment.scss";

// ── Reveal Hook ──────────────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return [ref, visible];
}

// ── Stagger Item ─────────────────────────────────────────────────────────────
function StaggerItem({ children, delay = 0, visible }) {
  return (
    <li
      className={visible ? "visible" : ""}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      <span className="cc-dot" />
      {children}
    </li>
  );
}

// ── Feature Card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, delay = 0, visible }) {
  return (
    <div
      className={`cc-feature-card ${visible ? "visible" : ""}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      <div className="cc-feature-card__icon">{icon}</div>
      <div className="cc-feature-card__title">{title}</div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function ResearchAndDevelopment() {
  pageTitle("Research & Development");

  const [heroRef, heroVisible] = useReveal();
  const [wayImgRef, wayImgVisible] = useReveal();
  const [wayContentRef, wayContentVisible] = useReveal();
  const [beyondContentRef, beyondContentVisible] = useReveal();
  const [beyondImgRef, beyondImgVisible] = useReveal();

  

  return (
    <>
      <BannerSectionStyle3
        bgUrl="/images/about/banner_bg.jpg"
        imgUrl="/images/about/banner_img.png"
        title="CELBION CULTURE"
      />

      <div className="cc-page">
        {/* HERO */}
        <section className="cc-hero" ref={heroRef}>
          <div className="cc-hero__noise" />
          <div className="cc-hero__orb cc-hero__orb--1" />
          <div className="cc-hero__orb cc-hero__orb--2" />

          <div className="cc-hero__content">
            <div className={`cc-hero__eyebrow ${heroVisible ? "visible" : ""}`}>
              Our Identity
            </div>

            <h1 className={`cc-hero__title ${heroVisible ? "visible" : ""}`}>
              Celbion <br />
              <span>Culture</span>
            </h1>

            <div className={`cc-hero__line ${heroVisible ? "visible" : ""}`} />

            <p className={`cc-hero__sub ${heroVisible ? "visible" : ""}`}>
              Rooted in science. Driven by ethics. <br />
              Built for long-term clinical trust.
            </p>
          </div>

          <div className="cc-hero__visual">
            <img src="/images/about/banner_bg.jpg" alt="" />
          </div>
        </section>

        {/* THE WAY */}
        <section className="cc-section">
          <div className="cc-way">
            <div className="cc-way__img-wrap" ref={wayImgRef}>
              <img
                src="/images/about/researchanddevelopment.png"
                alt="Research"
              />
              <div className="cc-way__img-badge">
                <strong>3+</strong> Core Principles
              </div>
            </div>

            <div className="cc-way__content" ref={wayContentRef}>
              <div className="cc-label">Philosophy</div>
              <h2 className="cc-way__heading">The Celbion Way</h2>

              <p className="cc-way__sub">
                Our culture is built around{" "}
                <strong>responsibility, learning & integrity.</strong>
              </p>

              <ul className="cc-pill-list">
                {[
                  "Medical science is continuously updated",
                  "Ethical promotion is non-negotiable",
                  "Feedback from clinicians is valued",
                  "Long-term credibility over short-term growth",
                ].map((text, i) => (
                  <StaggerItem
                    key={i}
                    delay={i * 100}
                    visible={wayContentVisible}
                  >
                    {text}
                  </StaggerItem>
                ))}
              </ul>

              <div className="cc-quote-card">
                Quiet confidence — letting science speak louder than claims.
              </div>
            </div>
          </div>
        </section>

        {/* BEYOND */}
        <section className="cc-section">
          <div className="cc-beyond">
            <div className="cc-beyond__content" ref={beyondContentRef}>
              <h2 className={beyondContentVisible ? "visible" : ""}>Beyond Medicines</h2>

              <div className={`cc-beyond__overview ${beyondContentVisible ? "visible" : ""}`}>
                <h3 className="cc-beyond__overview__heading">What Celbion Offers Beyond Medicines (overview)</h3>

                <h4 className="cc-beyond__overview__sub">Our Commitment Beyond Products</h4>

                <p className="cc-beyond__overview__text">
                  Celbion recognizes that modern medical practice requires more than prescriptions. To truly improve outcomes and patient trust, clinicians need <strong>scientific, educational, digital, and community-level support.</strong>
                </p>

                <p className="cc-beyond__overview__text">Celbion's ecosystem of deliverables is designed to:</p>

                <ul className="cc-beyond__overview__list">
                  <li>Support clinical decision-making</li>
                  <li>Improve patient understanding and compliance</li>
                  <li>Enhance clinic credibility and footfall</li>
                  <li>Strengthen doctor-patient relationships</li>
                </ul>
              </div>
            </div>

            <div className="cc-beyond__img-wrap" ref={beyondImgRef}>
              <img src="/images/about/beyond-medicine.png" alt="Beyond" />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}